import React, { Component, Fragment } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { submit } from 'redux-form';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import Layout from 'components/common/Layout';
import Progress from 'components/common/Progress';

import DocumentPreviewFillForm from 'components/common/DocumentPreviewFillForm';
import PaymentContractHeader from 'components/settings/PaymentContractHeader';
import DocumentsSignDialog from 'components/common/DocumentsSignDialog';

import { appTitle, titleSeparator } from 'config/helmetSettings';

import documentToPDF from 'utils/documentToPDF';
import documents from 'config/documents';

import { fetchBusiness, updateBusiness } from 'redux/modules/business';
import { selectPaymentPlan } from 'redux/modules/paymentPlans';

const document = documents.find(item => item.ID === 30);

const styles = () => ({
  paperLoadingStripe: {
    top: '50vh',
    left: '50%',
    position: 'fixed',
    marginLeft: -10,
    marginTop: 20,
    zIndex: 100,
    padding: '80px 24px 24px',
  },
  progressLoadingStripe: {
    top: 44,
  },
});

class PaymentContract extends Component {
  constructor(props) {
    super(props);

    this.state = {
      documentImages: null,
      signDialogOpen: false,
      loadingStripe: false,
    };

    this.importDependencies = this.importDependencies.bind(this);
    this.onSignButtonClick = this.onSignButtonClick.bind(this);
    this.onSignDialogClose = this.onSignDialogClose.bind(this);
    this.onSignDialogSubmitSuccess = this.onSignDialogSubmitSuccess.bind(this);
  }

  componentDidMount() {
    const { location, history, actions } = this.props;
    actions.fetchBusiness({
      triggerLoading: true,
    });
    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({ isMounted: true });
    this.importDependencies();
  }

  componentWillUnmount() {
    this.setState({ isMounted: false });
  }

  async importDependencies() {
    const documentImages = [];
    const pageImagesPromises = document.Pages.map((page, index) => (
      import(`../../${page.imageUrl}`)
        .then((image) => {
          documentImages[index] = image.default;
        })));
    Promise.all(pageImagesPromises).then(() => {
      if (this.state.isMounted) {
        this.setState({
          documentImages,
        });
      }
    });
  }

  onSignButtonClick() {
    this.setState({ signDialogOpen: true });
  }

  onSignDialogClose() {
    this.setState({ signDialogOpen: false });
  }

  onSignDialogSubmitSuccess(values) {
    const { actions } = this.props;
    this.setState({
      signDialogOpen: false,
      signature: values.signature,
    });
    this.onStripeLoadStart();
    setTimeout(() => {
      actions.submit(`${document.ID}-documentPreviewFill`);
    }, 500);   
  }

  onStripeLoadStart() {
    this.setState({ loadingStripe: true });
  }

  render() {
    const {
      business,
      loading,
      history,
      actions,
      location,
      classes,
    } = this.props;
    const { documentImages, signDialogOpen, signature, loadingStripe } = this.state;
    const documentWithImages = document && documentImages ? {
      ...document,
      Pages: document.Pages.map((page, index) => ({
        ...page,
        image: documentImages[index],
      })),
    } : null;
    let initialValues = {
      'Show Background Template': true,
    };
    if (business) {
      const fields = document.Pages.reduce((accumulator, currentValue) => ([
        ...accumulator,
        ...currentValue.fields ? currentValue.fields : [],
      ]), []);
      initialValues = fields.reduce((accumulator, currentValue) => ({
        ...accumulator,
        ...{ [currentValue.name]:
          currentValue.getValue
            ? currentValue.getValue({
              business,
              payment: {
                'Unit Amount': location.state['Unit Amount'],
              },
            }) : null },
      }), {
        Name: document.Name,
        'Show Charge': true,
        'Show Background Template': true,
      });
    }
    return (
      <Layout variant="settings">
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Payment Contract
          </title>
        </Helmet>
        {loadingStripe &&
          <Paper className={classes.paperLoadingStripe}>
            <Progress className={classes.progressLoadingStripe} />
            <Typography variant="subheading">
              Loading your payment in Stripe...
            </Typography>
          </Paper>}
        {(document && documentImages && loading === 0) ? (
          <Fragment>
            <PaymentContractHeader
              history={history}
              onSignButtonClick={this.onSignButtonClick}
              disabled={loadingStripe}
            />
            <DocumentsSignDialog
              open={signDialogOpen}
              onClose={this.onSignDialogClose}
              variant="default"
              showDate={false}
              onSubmitSuccess={this.onSignDialogSubmitSuccess}
            />
            <DocumentPreviewFillForm
              form={`${document.ID}-documentPreviewFill`}
              showHeader={false}
              variant="_blank"
              document={documentWithImages}
              initialValues={initialValues}
              business={business}
              payment={{
                'Unit Amount': location.state['Unit Amount'],
              }}
              disabled={loadingStripe}
              onSubmit={(values) => documentToPDF({
                  document: documentWithImages,
                  business,
                  payment: {
                    'Unit Amount': location.state['Unit Amount'],
                  },
                  values,
                  ...signature ? {
                    signature,
                  } : {},
                }).then(PDF => ({
                  blob: PDF.output('blob'),
                  Name: values.Name,
                }))}
              onSubmitCustom={(values) => documentToPDF({
                  document: documentWithImages,
                  business,
                  payment: {
                    'Unit Amount': location.state['Unit Amount'],
                  },
                  values,
                  ...signature ? {
                    signature,
                  } : {},
                }).then(PDF => ({
                  blob: PDF.output('blob'),
                  Name: values.Name,
                }))}
              onSubmitSuccess={(values) => {
                if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                  const fileBlob = values.blob;
                  fileBlob.lastModifiedDate = new Date();
                  actions.updateBusiness({
                    values: {
                      ID: business.ID,
                      Agreement: fileBlob,
                    },
                    resolve: () => {
                      actions.selectPaymentPlan({
                        values: {
                          'Price ID': Number(location.state['Price ID']),
                        },
                        resolve: (values) => {
                          const redirectUrl = new URL(values.url);
                          window.location.replace(redirectUrl);
                        },
                      });
                    },
                  });
                } else {
                  const file = new File(
                    [values.blob],
                    `${values.Name}.pdf`,
                    { type: 'application/pdf', lastModified: Date.now() },
                  );
                  actions.updateBusiness({
                    values: {
                      ID: business.ID,
                      Agreement: file,
                    },
                    resolve: () => {
                      actions.selectPaymentPlan({
                        values: {
                          'Price ID': Number(location.state['Price ID']),
                        },
                        resolve: (values) => {
                          const redirectUrl = new URL(values.url);
                          window.location.replace(redirectUrl);
                        },
                      });
                    },
                  });
                }
              }}
            />
          </Fragment>
        ) : <Progress />}
      </Layout>
    );
  }
}

PaymentContract.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
};

const mapStateToProps = store => ({
  business: store.business.data,
  loading: store.business.loading,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchBusiness,
    updateBusiness,
    selectPaymentPlan,
    submit,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(
  withStyles(styles)(PaymentContract)
);



// WEBPACK FOOTER //
// ./src/pages/settings/PaymentContract.js